import { useState } from 'react';
import {
  Avatar,
  Badge,
  Icon,
  SegmentedControl,
  SettingGroup,
  SettingRow,
  Sheet,
  Switch,
} from '@astral/ui';
import { LANGUAGE_META, type Language } from '@astral/shared';
import { hapticSelection } from '@astral/telegram/webapp';
import { useAuth, useCurrentUser } from '../providers/AuthProvider.js';
import { useT } from '../providers/I18nProvider.js';
import { SubHeader } from '../components/SubHeader.js';
import { RoleBadge } from '../components/RoleBadge.js';
import { LanguagePicker } from '../components/LanguagePicker.js';

const APP_VERSION = '0.1.0';

/**
 * Settings: language, notifications, profile.
 *
 * There is no appearance/theme section: Astral ships one official visual
 * identity, locked in `App`, so there is nothing here for a user to switch.
 */
export function SettingsScreen(): JSX.Element {
  const t = useT();
  const user = useCurrentUser();
  const { updatePreferences } = useAuth();

  const [languageSheet, setLanguageSheet] = useState(false);

  const setLanguage = (language: Language): void => {
    hapticSelection();
    void updatePreferences({ language });
  };

  const toggleNotifications = (enabled: boolean): void => {
    void updatePreferences({ notificationsEnabled: enabled });
  };

  return (
    <div className="screen screen--with-nav">
      <SubHeader title={t('settings.title')} />

      {/* Profile */}
      <SettingGroup title={t('settings.profile')}>
        <SettingRow
          icon={<Avatar name={user.firstName} src={user.photoUrl} size={34} />}
          title={`${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`}
          subtitle={user.username ? `@${user.username}` : undefined}
          trailing={<RoleBadge role={user.role} />}
        />
      </SettingGroup>

      {/* Language */}
      <SettingGroup title={t('settings.language')}>
        <SettingRow
          icon={<Icon name="globe" size={18} />}
          title={t('settings.language')}
          subtitle={`${LANGUAGE_META[user.language].flag} ${LANGUAGE_META[user.language].nativeName}`}
          trailing={<Icon name="chevron-right" size={18} />}
          onClick={() => setLanguageSheet(true)}
        />
      </SettingGroup>

      {/* Notifications */}
      <SettingGroup title={t('settings.notifications')}>
        <SettingRow
          icon={<Icon name="bell" size={18} />}
          title={t('settings.notifications')}
          subtitle={t('settings.notificationsDesc')}
          trailing={
            <Switch
              checked={user.notificationsEnabled}
              onChange={toggleNotifications}
              aria-label={t('settings.notifications')}
            />
          }
        />
      </SettingGroup>

      {/* About */}
      <SettingGroup title={t('settings.about')}>
        <SettingRow
          icon={<Icon name="sparkles" size={18} />}
          title={t('common.appName')}
          trailing={<Badge>{`${t('settings.version')} ${APP_VERSION}`}</Badge>}
        />
      </SettingGroup>

      {/* Language sheet */}
      <Sheet open={languageSheet} onClose={() => setLanguageSheet(false)} title={t('settings.language')}>
        <LanguagePicker
          value={user.language}
          onSelect={(language) => {
            setLanguage(language);
            setLanguageSheet(false);
          }}
        />
      </Sheet>
    </div>
  );
}
